// generateVideoObjectJsonLd.js
import {
  getPostVideoObject,
  getPostPreviewVideoObject,
  getCoporation,
  getPerson,
  getInteractionCounter,
} from './SchemaObject.js';
import {
  localeConvertor,
  DEFAULT_LANGUAGE,
  PREFERRED_LOCALES,
} from '../resource/i18nCommon.js';
import { getLanguageTranslationKey } from '../resource/getCurrentLanguage.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';
import { getI18nInstance } from '../resource/i18n.js';

/**
 * Convert locale to BCP47 format
 * @param {string} lang - Language code
 * @returns {string} BCP47 formatted language code
 */
const localeToBCP47 = lang => localeConvertor({ locale: lang, isBCP47: true });

/**
 * Build name/value object with language tag
 * @param {string} lang - Language code
 * @param {Function} translateFn - Translation function that takes language code
 * @returns {Object} Object with @value and @language properties
 */
const buildNameValueByLang = (lang, translateFn) => ({
  '@value': translateFn(lang),
  '@language': localeToBCP47(lang),
});

/**
 * Build array of alternate values for all alternate languages
 * @param {Array<string>} alternateLanguages - Array of alternate language codes
 * @param {Function} translateFn - Translation function that takes language code
 * @returns {Array<Object>} Array of objects with @value and @language properties
 */
const buildAlternateValues = (alternateLanguages, translateFn) =>
  alternateLanguages.map(lang => buildNameValueByLang(lang, translateFn));

/**
 * Generate alternate language objects for multilingual support
 * @param {Object} params
 * @param {Array<string>} params.supportedLanguages - Array of supported language codes
 * @param {Array<string>} params.alternateLanguages - Array of alternate language codes (excluding default)
 * @param {string} params.i18nTitleKey - i18n translation key for title
 * @param {string} params.i18nDescriptionKey - i18n translation key for description
 * @param {string} params.title - Default title
 * @param {string} params.description - Default description
 * @param {Function} params.t - Translation function
 * @returns {Object} Object containing inLanguage, name, alternateName, description, alternateDescription
 */
const getAlternateObject = ({
  supportedLanguages,
  alternateLanguages,
  i18nTitleKey,
  i18nDescriptionKey,
  title,
  description,
  t,
}) => {
  // Helper function to get translated title by language
  const getTitleByLang = lang =>
    t(i18nTitleKey, {
      title,
      lng: lang,
      ns: TranslationNamespace.SEO,
    });

  // Helper function to get translated description by language
  const getDescriptionByLang = lang =>
    t(i18nDescriptionKey, {
      caption: description || title,
      lng: lang,
      ns: TranslationNamespace.SEO,
    });

  return {
    inLanguage: supportedLanguages.map(lang => ({
      '@type': 'Language',
      name: t(getLanguageTranslationKey({ language: lang }), {
        lng: DEFAULT_LANGUAGE,
        ns: TranslationNamespace.SEO,
      }),
      alternateName: localeToBCP47(lang),
    })),
    name: buildNameValueByLang(DEFAULT_LANGUAGE, getTitleByLang),
    alternateName: buildAlternateValues(alternateLanguages, getTitleByLang),
    description: buildNameValueByLang(DEFAULT_LANGUAGE, getDescriptionByLang),
    alternateDescription: buildAlternateValues(
      alternateLanguages,
      getDescriptionByLang
    ),
  };
};

/**
 * Generate VideoObject JSON-LD for video detail pages with multilingual support
 * @param {Object} params
 * @param {string} params.id - Message ID
 * @param {string} params.title - Video title
 * @param {string} params.description - Video description
 * @param {number} params.durationSecs - Video duration in seconds
 * @param {string} params.senderId - Sender user ID
 * @param {string} params.username - Author username
 * @param {number} params.postedAtUnix - Posted timestamp
 * @param {boolean} params.isFree - Whether video is free
 * @param {number} params.unlockPrice - Unlock price if not free
 * @param {Array} params.interactionStatistic - Array of interaction statistics {type, count}
 * @param {string} params.thumbnailUrl - Thumbnail URL
 * @param {Array<string>} params.hashtags - Array of hashtags
 * @param {Array<string>} params.categories - Array of categories
 * @param {string} params.origin - URL origin (default: 'https://swag.live')
 * @param {string} params.sourceLanguage - Source language code
 * @param {Array<string>} params.assetIds - Array of asset IDs
 * @param {Array<string>} params.relatedMessageIds - Array of related message IDs
 * @param {Array<string>} params.supportedLanguages - Array of supported language codes (optional, will use all available languages if not provided)
 * @param {Object} params.i18nKeys - Object containing i18n keys {title, description, previewTitle, previewDescription}
 * @param {string} params.offerDescription - Description for the offer (optional)
 * @returns {Promise<Object>} JSON-LD object with @graph structure
 */
export const generateVideoObjectJsonLd = async ({
  id,
  title,
  description,
  durationSecs,
  senderId,
  username,
  postedAtUnix,
  isFree,
  unlockPrice,
  interactionStatistic = [],
  thumbnailUrl,
  hashtags = [],
  categories = [],
  origin = 'https://swag.live',
  sourceLanguage,
  assetIds = [],
  relatedMessageIds = [],
  supportedLanguages,
  i18nKeys = {
    title: 'seo_video_detail_title',
    description: 'seo_video_detail_description',
    previewTitle: 'seo_video_detail_preview_title',
    previewDescription: 'seo_video_detail_preview_description',
  },
  offerDescription,
}) => {
  // Get i18n instance
  const { i18n } = await getI18nInstance();
  const t = i18n.t;

  // Use all available languages if not provided
  // Default to PREFERRED_LOCALES which includes all supported languages
  const languages = supportedLanguages || PREFERRED_LOCALES;
  const alternateLanguages = languages.filter(
    lang => DEFAULT_LANGUAGE !== lang
  );

  // Fetch each alternate language translations in SEO group
  if (i18n) {
    const loadLanguagePromises = alternateLanguages.map(async lang => {
      if (!i18n.hasResourceBundle(lang, TranslationNamespace.SEO)) {
        await i18n.reloadResources(lang, TranslationNamespace.SEO);
      }
    });
    await Promise.all(loadLanguagePromises);
  }

  // Convert interaction statistics to InteractionCounter format
  const formattedInteractionStatistic = interactionStatistic.map(
    getInteractionCounter
  );

  // Generate alternate objects for multilingual support
  const previewAlternateObject = getAlternateObject({
    supportedLanguages: languages,
    alternateLanguages,
    i18nTitleKey: i18nKeys.previewTitle,
    i18nDescriptionKey: i18nKeys.previewDescription,
    title,
    description,
    t,
  });

  const fullAlternateObject = getAlternateObject({
    supportedLanguages: languages,
    alternateLanguages,
    i18nTitleKey: i18nKeys.title,
    i18nDescriptionKey: i18nKeys.description,
    title,
    description,
    t,
  });

  // Generate preview video object with multilingual support
  const postPreviewVideoObject = {
    ...getPostPreviewVideoObject({
      id,
      assetId: assetIds[0],
      title,
      description,
      postedAtUnix,
      sourceLanguage,
      author: {
        '@id': `${origin}/user/${senderId}#person`,
      },
      thumbnailUrl,
      origin,
    }),
    ...previewAlternateObject,
  };

  // Generate full video object with multilingual support
  const postVideoObject = {
    ...getPostVideoObject({
      id,
      assetId: assetIds[0],
      username,
      title,
      description,
      durationSecs,
      postedAtUnix,
      isFree,
      unlockPrice,
      author: {
        '@id': `${origin}/user/${senderId}#person`,
      },
      interactionStatistic: formattedInteractionStatistic,
      thumbnailUrl,
      hashtags,
      categories,
      origin,
      sourceLanguage,
    }),
    ...fullAlternateObject,
    // Override offers description if provided
    ...(offerDescription && !isFree
      ? {
          offers: {
            '@type': 'Offer',
            price: unlockPrice,
            priceCurrency: 'XXX',
            availability: 'https://schema.org/InStock',
            category: 'Pay-per-view',
            description: offerDescription,
          },
        }
      : {}),
    // Add about field for categories
    ...(categories.length > 0
      ? {
          about: categories.map(category => ({
            '@type': 'Thing',
            name: category,
            url: `${origin}/category/${encodeURIComponent(category)}/flix`,
          })),
        }
      : {}),
  };

  // Helper function to get home title by language
  const getHomeTitleByLang = lang =>
    t('seo-home-title', {
      lng: lang,
      ns: TranslationNamespace.SEO,
    });

  // Generate breadcrumb list
  const breadcrumbList = {
    '@type': 'BreadcrumbList',
    '@id': `${origin}/post/${id}#breadcrumb`,
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: buildNameValueByLang(DEFAULT_LANGUAGE, getHomeTitleByLang),
        alternateName: buildAlternateValues(
          alternateLanguages,
          getHomeTitleByLang
        ),
        item: {
          '@id': `${origin}/#website`,
        },
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: postVideoObject.name,
        alternateName: postVideoObject.alternateName,
        item: { '@id': `${origin}/post/${id}#webpage` },
      },
    ],
  };

  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        '@id': `${origin}/post/${id}#webpage`,
        url: `${origin}/post/${id}`,
        breadcrumb: { '@id': `${origin}/post/${id}#breadcrumb` },
        mainEntity: { '@id': `${origin}/post/${id}#full` },
        inLanguage: postVideoObject.inLanguage,
        name: postVideoObject.name,
        alternateName: postVideoObject.alternateName,
        description: postVideoObject.description,
        alternateDescription: postVideoObject.alternateDescription,
      },
      {
        '@type': 'BlogPosting',
        headline: title,
        articleBody: description,
        sharedContent: {
          '@id': postVideoObject['@id'],
        },
        image: [postVideoObject.thumbnailUrl],
        datePublished: postVideoObject.uploadDate,
        author: {
          '@id': `${origin}/user/${senderId}#person`,
        },
        publisher: getCoporation({ origin }),
        relatedLink: relatedMessageIds.map(
          messageId => `${origin}/post/${messageId}`
        ),
      },
      postPreviewVideoObject,
      postVideoObject,
      getPerson({ id: senderId, username, origin }),
      breadcrumbList,
    ],
  };
};

export default generateVideoObjectJsonLd;
